import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../components/ui/accordion';
import { Badge } from '../../../components/ui/badge';
import { getNoteTypeFullName, formatNoteHTML, getNoteTypeBadgeVariant } from '../../../utils/noteTypeMapper';

/**
 * Parse note text into table rows by splitting on colon
 * @param {string} noteText - The note text to parse
 * @returns {Array} Array of {label, value} objects or null if no colons found
 */
const parseNoteToTable = (noteText) => {
  if (!noteText) return null;
  
  // Split by newlines to get individual lines
  const lines = noteText.split('\n').filter(line => line.trim());
  
  // Check if lines contain colons - if not, return null to display as plain text
  const hasColons = lines.some(line => line.includes(':'));
  if (!hasColons) return null;
  
  // Parse each line that contains a colon
  const rows = lines
    .filter(line => line.includes(':'))
    .map(line => {
      const colonIndex = line.indexOf(':');
      const label = line.substring(0, colonIndex).trim();
      const value = line.substring(colonIndex + 1).trim();
      return { label, value };
    })
    .filter(row => row.label && row.value); // Only include rows with both label and value
  
  return rows.length > 0 ? rows : null;
};

/**
 * CoverageNotesDisplay - Displays plan coverage notes in accordion format with table layout
 */
const CoverageNotesDisplay = ({ notes }) => {
  if (!notes || notes.length === 0) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground text-center">Coverage notes not provided by plan</p>
      </div>
    );
  }

  return (
    <Accordion type="multiple" className="w-full">
      {notes.map((note, index) => {
        const noteTypeAbbr = note.NoteType?.name || '';
        const noteTypeFullName = getNoteTypeFullName(noteTypeAbbr);
        const formattedNote = formatNoteHTML(note.Note);
        const variant = getNoteTypeBadgeVariant(noteTypeAbbr);
        
        // Try to parse as table
        const tableRows = parseNoteToTable(formattedNote);

        return (
          <AccordionItem key={index} value={`note-${index}`}>
            <AccordionTrigger className="hover:no-underline text-xs sm:text-sm">
              <div className="flex items-center gap-2">
                <Badge variant={variant} className="text-xs">{noteTypeAbbr}</Badge>
                <span className="font-medium">{noteTypeFullName}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              {tableRows ? (
                // Display as table if colon-delimited format detected
                <div className="bg-muted rounded-lg overflow-hidden mt-2">
                  <table className="w-full text-xs">
                    <tbody>
                      {tableRows.map((row, rowIndex) => (
                        <tr key={rowIndex} className="border-b border-border last:border-b-0">
                          <td className="font-semibold text-foreground p-3 align-top w-1/3 bg-muted/50">
                            {row.label}
                          </td>
                          <td className="text-foreground p-3 align-top">
                            {row.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                // Fallback to plain text display if no colon format
                <div className="bg-muted rounded-lg p-3 mt-2">
                  <pre className="whitespace-pre-wrap text-xs font-sans text-foreground">
                    {formattedNote}
                  </pre>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
};

export default CoverageNotesDisplay;
